import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { isRunningLocal, builtEnvironment, SessionType } from '@shared';
import { DialogModal } from '@client/components';
import { Button } from '@client/components/ui/button';
import { useDispatch, useSelector } from 'react-redux';
import { closeSession } from '@client';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { selectSession } from '@client/model';
import { AgentEnteredWaitingCard } from '@client/features/invite-client';
import {
  usePrefillReview,
  useCompletePrefillReview,
  PrefillReviewScreen,
  resolvePrefillStep,
} from '@client/features/prefill-review';

export function OnboardingFormPage() {
  const { formReferenceId } = useParams<{ formReferenceId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Carry the channel tag through to the open-time resolver (and the logout-retry hop).
  const source = searchParams.get('source');
  const sourceQuery = source ? `?source=${encodeURIComponent(source)}` : '';
  const { getClerkToken, clerkSignOut } = useClerkClientAuth();
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const [error, setError] = useState<string | null>(null);
  const [isForbiddenError, setIsForbiddenError] = useState(false);
  const [agentEnteredIntakeId, setAgentEnteredIntakeId] = useState<string | null>(null);
  const [isAgentEnteredConsentRequired, setIsAgentEnteredConsentRequired] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);

  const isClientSession = session.sessionType === SessionType.client;
  const { data: prefillReview, isLoading: isPrefillReviewLoading } = usePrefillReview(
    formReferenceId,
    isClientSession,
  );
  const completePrefillReview = useCompletePrefillReview(formReferenceId);

  const prefillStep = resolvePrefillStep({ review: prefillReview, reviewAcknowledged });
  const shouldShowPrefillReview = prefillStep !== 'form';

  const handleContinueFromReview = async () => {
    if (prefillStep === 'review') {
      try {
        await completePrefillReview.mutateAsync();
      } catch {
        // The mutation displays the failure toast. Keep the review visible so the client can retry.
        return;
      }
    }
    setReviewAcknowledged(true);
  };

  const handleLogoutAndRetry = () => {
    if (!formReferenceId) {
      return;
    }

    dispatch(closeSession());
    const onboardingUrl = `/onboarding/${formReferenceId}${sourceQuery}`;
    clerkSignOut({ redirectUrl: onboardingUrl });
  };

  useEffect(() => {
    async function fetchFormLink() {
      try {
        const token = await getClerkToken();
        if (!token) {
          setError('Authentication required. Please sign in.');
          return;
        }
        const env = isRunningLocal() ? 'dev' : builtEnvironment;
        const response = await fetch(
          `/${env}/api/onboarding-form/${formReferenceId}${sourceQuery}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          const statusCode = response.status;
          const errorStatusCode = errorData.error?.metadataFlattened?.statusCode;
          const errorMessage =
            errorData.errorMessage ||
            errorData.error?.messages?.[0] ||
            errorData.error ||
            'Failed to load form';

          // The 409 the consent gate raises, alongside the `prefillReviewRequired` precedent it
          // was modelled on. Without recognising it the advisor sees an unexplained
          // "Failed to load form" and has no way to tell what is being waited on.
          const metadata = errorData.error?.metadataFlattened ?? {};
          if (statusCode === 409 && metadata.agentEnteredConsentRequired) {
            setIsAgentEnteredConsentRequired(true);
            setAgentEnteredIntakeId(metadata.agentEnteredIntakeId ?? null);
            return;
          }

          const isPermissionError =
            [403, 405].includes(statusCode) ||
            [403, 405, '403', '405'].includes(errorStatusCode) ||
            (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('permission'));

          setError(errorMessage);
          if (isPermissionError) {
            setIsForbiddenError(true);
          }
          return;
        }

        const { formLink, isCompleted: completed } = await response.json();

        if (completed) {
          setIsCompleted(true);
        } else {
          window.location.href = formLink;
        }
      } catch (err) {
        console.error('Error fetching form link:', err);
        setError('An error occurred while loading your form');
      }
    }

    if (formReferenceId && !isPrefillReviewLoading && !shouldShowPrefillReview) {
      fetchFormLink();
    }
  }, [
    formReferenceId,
    sourceQuery,
    getClerkToken,
    isPrefillReviewLoading,
    shouldShowPrefillReview,
  ]);

  if (isPrefillReviewLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (shouldShowPrefillReview && prefillReview) {
    return (
      <PrefillReviewScreen
        review={prefillReview}
        isSubmitting={completePrefillReview.isPending}
        showAnswers={prefillStep === 'review'}
        onContinue={handleContinueFromReview}
      />
    );
  }

  if (isAgentEnteredConsentRequired) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-950">
              Waiting on client consent
            </h1>
            <p className="text-muted-foreground mt-3 text-base leading-relaxed">
              Your client needs to give permission before you can complete this questionnaire on
              their behalf.
            </p>
          </div>

          {agentEnteredIntakeId ? (
            <AgentEnteredWaitingCard requestId={agentEnteredIntakeId} />
          ) : (
            <p className="text-muted-foreground text-center text-sm">
              Open the client&apos;s record to resend the permission request.
            </p>
          )}

          <div className="flex justify-center">
            <Button
              variant="secondary"
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Form Already Completed</h1>
          <p className="mb-6 text-gray-700">
            You've already completed this intake form. If you need to make changes, please contact
            your advisor.
          </p>
          <button
            onClick={() => navigate('/')}
            className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isForbiddenError) {
    return (
      <DialogModal
        open={true}
        onClose={() => {}}
        title="Access Denied"
        subTitle=""
        width="md"
        primaryButton={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => navigate('/')}
              className="w-full focus-visible:ring-0 focus-visible:ring-offset-0 sm:w-auto"
            >
              Go Home
            </Button>
            <Button
              variant="default"
              onClick={handleLogoutAndRetry}
              className="w-full focus-visible:ring-0 focus-visible:ring-offset-0 sm:w-auto"
            >
              Log Out and Try Again
            </Button>
          </div>
        }
      >
        <div className="my-5">
          <div className="mb-6 flex justify-center">
            <svg
              className="h-16 w-16 text-red-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="space-y-3 text-center">
            <p className="text-sm text-gray-700">
              {error || 'You do not have permission to access this form.'}
            </p>
            <p className="text-sm font-medium text-gray-900">
              Do you want to log out and try again?
            </p>
          </div>
        </div>
      </DialogModal>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-red-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Error</h1>
          <p className="mb-6 text-gray-700">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600"></div>
      </div>
    </div>
  );
}
